import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/tubing/canyon-tubing-scene.jpg";
import cabanaImage from "@/assets/cabanas/cabana-riverside.jpg";
import familyImage from "@/assets/tubing/family-tubing-fun.jpg";
import aerialImage from "@/assets/gallery/aerial-cabanas.jpg";

const PUBLISH_DATE = "May 5, 2026";
const CANONICAL = "/blog/tubing-guadalupe-river-new-braunfels";
const TITLE =
  "Tubing the Guadalupe River, New Braunfels Guide | Son's";
const DESCRIPTION =
  "Tubing the Guadalupe River near New Braunfels, TX: cabana rentals, family-friendly floats, kayaking & the best things to do with kids. Book at Son's.";

const schemaJson = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline:
    "Tubing the Guadalupe River Near New Braunfels — The Ultimate Family Guide",
  description: DESCRIPTION,
  image: "https://sonsguadalupe.com/og-image.png",
  author: { "@type": "Organization", name: "Son's Guadalupe" },
  publisher: {
    "@type": "Organization",
    name: "Son's Guadalupe",
    logo: {
      "@type": "ImageObject",
      url: "https://sonsguadalupe.com/og-image.png",
    },
  },
  datePublished: "2026-05-05",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://sonsguadalupe.com${CANONICAL}`,
  },
  keywords:
    "tubing the Guadalupe River near New Braunfels, Guadalupe River tubing, cabana rentals New Braunfels, family fun things to do in New Braunfels, kayaking Guadalupe River, fun things to do near New Braunfels, river floats Texas, Hill Country tubing",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "When is the best time to go tubing on the Guadalupe River near New Braunfels?",
      acceptedOffer: undefined,
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Guadalupe River tubing season near New Braunfels typically runs from late spring through early fall. May through September offers the warmest water and the most reliable flow for floating, while spring and early fall give you smaller crowds.",
      },
    },
    {
      "@type": "Question",
      name: "Are there cabana rentals near the Guadalupe River in New Braunfels?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Son's Guadalupe offers riverside cabana rentals in New Braunfels with shaded picnic tables, parking, and direct river access — perfect for a family day of tubing the Guadalupe River.",
      },
    },
    {
      "@type": "Question",
      name: "Is tubing the Guadalupe River family-friendly?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. Tubing the Guadalupe River is one of the most popular family fun things to do in New Braunfels. With calm stretches and gentle rapids, it's a great adventure for kids and adults alike.",
      },
    },
  ],
};

const BlogTubingGuadalupeNewBraunfels = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        schemaJson={schemaJson}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Header />
      <main className="pt-24 pb-16">
        {/* Hero */}
        <div className="relative h-[55vh] min-h-[400px]">
          <img
            src={heroImage}
            alt="Families tubing the Guadalupe River near New Braunfels, Texas on a sunny day"
            width={1600}
            height={900}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" />
                {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Tubing the Guadalupe River Near New Braunfels — The Ultimate
                Family Guide
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Looking for the best <strong>fun things to do near New
                Braunfels</strong>? There is one Texas tradition that has
                topped the list for generations — <strong>tubing the
                Guadalupe River near New Braunfels</strong>. Crystal-clear
                spring-fed water, towering cypress trees, gentle rapids, and
                that unmistakable Hill Country sunshine come together to
                create the perfect day on the river. At <strong>Son's
                Guadalupe</strong>, we have made it our mission to give
                families the easiest, most relaxing, and most memorable
                Guadalupe River tubing experience in New Braunfels, TX.
              </p>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                Why Tubing the Guadalupe River Is the #1 Thing to Do in New
                Braunfels
              </h2>
              <p>
                Ask anyone who grew up in Texas: a float on the Guadalupe is a
                rite of passage. <strong>Tubing the Guadalupe River near New
                Braunfels</strong> is more than just a summer activity — it is
                an experience. Whether you are searching for{" "}
                <em>family fun things to do in New Braunfels</em>, planning a
                bachelorette weekend, or just trying to escape the Texas heat
                with the kids, floating the Guadalupe checks every box.
              </p>
              <ul className="space-y-3 list-disc pl-6">
                <li>
                  <strong>Cool, clear, spring-fed water</strong> — even in the
                  middle of August.
                </li>
                <li>
                  <strong>Family-friendly stretches</strong> with calm pools
                  and small, fun rapids.
                </li>
                <li>
                  <strong>Stunning Hill Country scenery</strong> — cypress
                  trees, limestone bluffs, and wildlife.
                </li>
                <li>
                  <strong>Easy access</strong> from San Antonio, Austin, and
                  Houston.
                </li>
              </ul>

              <figure className="my-10">
                <img
                  src={familyImage}
                  alt="Kids smiling and laughing while tubing the Guadalupe River in New Braunfels"
                  width={1280}
                  height={800}
                  loading="lazy"
                  className="w-full rounded-2xl shadow-elevated"
                />
                <figcaption className="text-center text-sm text-muted-foreground mt-3">
                  Family fun things to do in New Braunfels — tubing on the
                  Guadalupe River.
                </figcaption>
              </figure>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                Guadalupe River Tubing & Cabana Rentals in New Braunfels
              </h2>
              <p>
                One of the things that sets Son's Guadalupe apart from other{" "}
                <strong>tubing outfitters in New Braunfels</strong> is our
                riverside <strong>cabana rentals</strong>. Instead of fighting
                for parking and dragging coolers across hot asphalt, you
                simply pull up, post up at your private cabana, and step
                straight onto the river. Our{" "}
                <strong>cabana rentals in New Braunfels</strong> are the
                ultimate home base for a day of tubing the Guadalupe River.
              </p>

              <figure className="my-10">
                <img
                  src={cabanaImage}
                  alt="Riverside cabana rental in New Braunfels with picnic table and Guadalupe River view"
                  width={1280}
                  height={800}
                  loading="lazy"
                  className="w-full rounded-2xl shadow-elevated"
                />
                <figcaption className="text-center text-sm text-muted-foreground mt-3">
                  Guadalupe River cabana rentals in New Braunfels — your
                  home base on the river.
                </figcaption>
              </figure>

              <h3 className="font-heading text-2xl font-bold text-foreground mt-8">
                What is Included With a Son's Guadalupe Cabana
              </h3>
              <ul className="space-y-3 list-disc pl-6">
                <li>Shaded private cabana for up to 6 guests</li>
                <li>Picnic table and direct river access</li>
                <li>One free parking spot</li>
                <li>Restrooms, river entry points, and on-site staff</li>
                <li>
                  Optional add-ons: <strong>tube rentals</strong>,{" "}
                  <strong>kayak rentals</strong>, and shuttle service
                </li>
              </ul>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                Family Fun Things to Do in New Braunfels Beyond Tubing
              </h2>
              <p>
                If you are searching for <strong>fun things to do near New
                Braunfels</strong> with the family, the Guadalupe River area
                has it all. Pair your day of tubing with any of these
                family-favorite activities:
              </p>
              <ul className="space-y-3 list-disc pl-6">
                <li>
                  <strong>Kayaking the Guadalupe River</strong> — perfect for
                  paddlers of all skill levels.
                </li>
                <li>
                  <strong>Riverside picnics</strong> at our cabana rentals.
                </li>
                <li>
                  <strong>Glamping on the Guadalupe River</strong> — extend
                  the adventure with an overnight stay.
                </li>
                <li>
                  <strong>Swimming holes and shallow play areas</strong> —
                  great for younger kids.
                </li>
                <li>
                  <strong>Fishing</strong> for bass and Guadalupe bass right
                  from the bank.
                </li>
                <li>
                  Day trips to <strong>Gruene Historic District</strong>,{" "}
                  <strong>Schlitterbahn</strong>, and{" "}
                  <strong>Natural Bridge Caverns</strong>.
                </li>
              </ul>

              <figure className="my-10">
                <img
                  src={aerialImage}
                  alt="Aerial view of the Guadalupe River winding through New Braunfels Texas Hill Country"
                  width={1280}
                  height={800}
                  loading="lazy"
                  className="w-full rounded-2xl shadow-elevated"
                />
                <figcaption className="text-center text-sm text-muted-foreground mt-3">
                  The Guadalupe River near New Braunfels — one of the most
                  scenic floats in Texas.
                </figcaption>
              </figure>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                Tips for Tubing the Guadalupe River Near New Braunfels
              </h2>
              <ol className="space-y-3 list-decimal pl-6">
                <li>
                  <strong>Book early.</strong> Summer weekends and holidays
                  fill up fast. Reserve your cabana and tubes online in
                  advance.
                </li>
                <li>
                  <strong>Arrive in the morning.</strong> Cooler temps, easier
                  parking, and a longer day on the river.
                </li>
                <li>
                  <strong>Bring water shoes.</strong> The riverbed has rocks —
                  protect those feet.
                </li>
                <li>
                  <strong>No glass, no styrofoam.</strong> Pack your cooler in
                  cans and reusable containers — it is the law on the
                  Guadalupe.
                </li>
                <li>
                  <strong>Sunscreen, then more sunscreen.</strong> Texas sun
                  reflects off the water — reapply often.
                </li>
                <li>
                  <strong>Bring a dry bag</strong> for phones, keys, and
                  wallets.
                </li>
              </ol>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                How to Get to Son's Guadalupe in New Braunfels, TX
              </h2>
              <p>
                Son's Guadalupe is conveniently located right on the Guadalupe
                River in <strong>New Braunfels, Texas</strong>, just minutes
                from I-35 and Highway 46. We are an easy drive from{" "}
                <strong>San Antonio (35 minutes)</strong>,{" "}
                <strong>Austin (1 hour)</strong>, and{" "}
                <strong>Houston (3 hours)</strong> — making us the perfect
                weekend escape for the whole state.
              </p>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                Frequently Asked Questions
              </h2>

              <h3 className="font-heading text-xl font-bold text-foreground mt-6">
                When is the best time to go tubing on the Guadalupe River?
              </h3>
              <p>
                The Guadalupe River tubing season near New Braunfels runs from
                late spring through early fall. May through September offers
                the warmest water and the most reliable flow.
              </p>

              <h3 className="font-heading text-xl font-bold text-foreground mt-6">
                Are there cabana rentals near the Guadalupe River?
              </h3>
              <p>
                Yes — Son's Guadalupe offers some of the best{" "}
                <strong>cabana rentals in New Braunfels</strong>, with shaded
                picnic tables, parking, and direct river access.
              </p>

              <h3 className="font-heading text-xl font-bold text-foreground mt-6">
                Is tubing the Guadalupe River family-friendly?
              </h3>
              <p>
                Absolutely. It is one of the top{" "}
                <strong>family fun things to do in New Braunfels</strong>,
                with calm stretches and gentle rapids that are great for kids
                and adults alike.
              </p>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">
                Ready to Float? Book Your Guadalupe River Tubing Day Today
              </h2>
              <p>
                There is a reason <strong>tubing the Guadalupe River near
                New Braunfels</strong> tops every Texan's summer bucket list.
                Whether you are coming for a day of floating, a family
                getaway, or a weekend of glamping, Son's Guadalupe is your
                home base on the river. Lock in your cabana, grab your tubes,
                and let's make memories on the Guadalupe.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/tubing">
                  <Button variant="default" size="lg" className="text-lg px-10 py-6">
                    Book Tubing Now
                  </Button>
                </Link>
                <Link to="/cabanas">
                  <Button variant="outline" size="lg" className="text-lg px-10 py-6">
                    Reserve a Cabana
                  </Button>
                </Link>
              </div>

              <p className="text-center text-muted-foreground text-sm mt-4">
                See you on the river! 🌊🛟☀️
              </p>
            </div>
          </div>
        </article>
      </main>
      <SEOContentBlock
        eyebrow="More on the Guadalupe"
        heading="Cabana Rentals, Glamping & Camping on the Guadalupe River"
        paragraphs={[
          "After a day of tubing the Guadalupe River, extend the adventure with cabana rentals near New Braunfels, glamping near New Braunfels in our riverside bell tents, or tent camping on River Road. It's the easiest way to turn a tubing day into a full family getaway near New Braunfels.",
          "Looking for more fun things to do with kids near New Braunfels? Pair your float with kayaking, riverside picnics, fishing, and visits to Gruene, Schlitterbahn, and Natural Bridge Caverns.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Glamping", to: "/glamping" },
          { label: "Property Map", to: "/property-map" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogTubingGuadalupeNewBraunfels;
